import type { ComponentType } from "react";

export type HelpCategory = "concepts" | "flows" | "glossary";
export type HelpLocale = "en" | "de";

export interface HelpArticleMeta {
  slug: string;
  title: string;
  category: HelpCategory;
  locale: HelpLocale;
  description?: string;
  keywords?: string[];
}

export interface HelpArticle extends HelpArticleMeta {
  load: () => Promise<{ default: ComponentType<unknown> }>;
}

interface HelpModule {
  default: ComponentType<unknown>;
  // `remark-mdx-frontmatter` exposes the YAML preamble as this named export.
  frontmatter?: Record<string, unknown>;
}

// Eagerly import the compiled MDX modules and read frontmatter from the
// `frontmatter` export that `remark-mdx-frontmatter` emits.
//
// We deliberately do NOT use `import.meta.glob(..., { query: "?raw" })` here:
// `@mdx-js/rollup` strips the query (`id.split('?')[0]`) before its extension
// check, so it compiles `*.mdx?raw` into a component in the production build.
// The "raw" import is then a function, not a string, and `parseFrontmatter`
// crashed with `e.match is not a function` at module load — blanking the
// whole app shell, since the navigation statically imports the help panel.
const modules = import.meta.glob("../../content/help/**/*.mdx", {
  eager: true,
}) as Record<string, HelpModule>;

const PATH_RE = /\/content\/help\/(en|de)\/(concepts|flows|glossary)\/([^/]+)\.mdx$/;

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function buildArticle(path: string, mod: HelpModule): HelpArticle | null {
  const match = path.match(PATH_RE);
  if (!match) return null;
  const [, locale, category, fileSlug] = match;
  const Component = mod.default;
  if (typeof Component !== "function") return null;

  const data = mod.frontmatter ?? {};
  const slug = asString(data.slug) ?? fileSlug;
  const title = asString(data.title) ?? slug;
  const keywords = Array.isArray(data.keywords)
    ? data.keywords.filter((k): k is string => typeof k === "string")
    : undefined;

  return {
    slug,
    title,
    category: category as HelpCategory,
    locale: locale as HelpLocale,
    description: asString(data.description),
    keywords,
    load: () => Promise.resolve({ default: Component }),
  };
}

const ALL_ARTICLES: HelpArticle[] = Object.entries(modules)
  .map(([path, mod]) => {
    // A single malformed article must never take down the app shell.
    try {
      return buildArticle(path, mod);
    } catch {
      return null;
    }
  })
  .filter((a): a is HelpArticle => a !== null);

export function getArticles(locale: HelpLocale): HelpArticle[] {
  return ALL_ARTICLES.filter((a) => a.locale === locale);
}

export function getArticle(locale: HelpLocale, slug: string): HelpArticle | undefined {
  return ALL_ARTICLES.find((a) => a.locale === locale && a.slug === slug);
}

export function getCategories(locale: HelpLocale): Record<HelpCategory, HelpArticle[]> {
  const articles = getArticles(locale);
  return {
    concepts: articles.filter((a) => a.category === "concepts"),
    flows: articles.filter((a) => a.category === "flows"),
    glossary: articles.filter((a) => a.category === "glossary"),
  };
}
